import { Fragment, useCallback } from "react";
import { useInfiniteListUnitGroup } from "../../../api/hooks";

import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { PropertyCard } from "../../components/property/PropertyCard";
import { ReadMore as ReadMoreIcon } from "@mui/icons-material";
import { Props } from "./Props";
import { useRecoilValue, useResetRecoilState } from "recoil";
import {
  filterSelectedAttributeIdsAtom,
  filterSelectedAttributeIdsSelector,
  sortOrderAtom
} from "../../../state/search-state";
import { EmptyContent } from "../../../fe-ui/blocks/EmptyContent/EmptyContent";
import { SkeletonUnitsList } from "../../components/SkeletonUnitsList/SkeletonUnitsList";
import { useTranslation } from "../../../fe-ui/hooks/use-translation";

const LoadingState = counter => (
  <Box display={"flex"} justifyContent={"center"} my={2}>
    <SkeletonUnitsList counter={counter} />
  </Box>
);

const ErrorState = () => (
  <Box display={"flex"} justifyContent={"center"} my={2}>
    <Button variant={"outlined"} color={"error"}>
      {"Error"}
    </Button>
  </Box>
);

export const UnitGroupListWidget = ({
  perPage = 12,
  infinite = true,
  hotelIdContext: hotelId,
  withAttributesContext,
  dataTestId,
}: // withGuestsContext
Props) => {
  const t = useTranslation();
  const resetAttributes = useResetRecoilState(filterSelectedAttributeIdsAtom);
  const selectedAttributes = useRecoilValue(filterSelectedAttributeIdsSelector);
  const sortOrder = useRecoilValue(sortOrderAtom);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } = useInfiniteListUnitGroup(
    hotelId,
    withAttributesContext ? selectedAttributes.join(",") : "",
    sortOrder
  );

  const handleLoadNext = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      fetchNextPage();
    },
    [fetchNextPage]
  );

  const loadNextIsDisabled = !hasNextPage || isFetchingNextPage;

  if (status === "loading") {
    return <LoadingState counter={perPage} />;
  }

  if (status === "error") {
    return <ErrorState />;
  }

  if (data === undefined || data?.pages?.[0].length === 0) {
    return (
      <EmptyContent
        filled
        title={t("search-nothing-found-title")}
        description={
          <>
            {t.rich("search-nothing-found-try-different-search", {
              br: () => <br />,
              clearfilter: x => (
                <Button onClick={resetAttributes} size="small" variant="text">
                  {x}
                </Button>
              ),
              changedates: () => <a />
            })}
          </>
        }
      />
    );
  }

  return (
    <>
      <Grid container spacing={4} data-testid={dataTestId}>
        {data.pages.map((group, i) => (
          <Fragment key={i}>
            {group.map(unit => (
              <Grid item key={unit.id} xs={12} sm={6} md={4} lg={3}>
                <PropertyCard
                  subtitle={unit.hotel?.title}
                  image={unit.media[0]?.url}
                  imageList={unit.media.reduce((acc: string[], media, idx, origArr) => {
                    if (acc.length > 9) {
                      origArr.splice(idx);
                      return acc;
                    }
                    return (media?.media_type === 'photo') ? [...acc, media?.url] : acc;
                  }, [])}
                  {...unit}
                  description={unit.shortDescription || unit.description}
                />
              </Grid>
            ))}
          </Fragment>
        ))}
      </Grid>
      {infinite && (
        <>
          <Box display={"flex"} justifyContent={"center"} my={2}>
            {hasNextPage && (
              <Button
                startIcon={!isFetchingNextPage && <ReadMoreIcon />}
                data-testid="load-more-button"
                color={"primary"}
                sx={{ borderRadius: 3 }}
                variant={"outlined"}
                onClick={handleLoadNext}
                disabled={loadNextIsDisabled}
              >
                {isFetchingNextPage ? (
                  <>
                    &nbsp;
                    <CircularProgress color={"inherit"} size={20} />
                    &nbsp;
                  </>
                ) : hasNextPage ? (
                  t("cta-load-more")
                ) : (
                  t("cta-load-more-ended")
                )}
              </Button>
            )}
            {!hasNextPage && <>{/* TODO: Final state */}</>}
          </Box>
        </>
      )}
    </>
  );
};
